import { NavLink, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import Dashboard from './pages/Dashboard';
import Explorer from './pages/Explorer';
import Editor from './pages/Editor';
import Drafts from './pages/Drafts';
import Snapshots from './pages/Snapshots';
import Audit from './pages/Audit';
import Resources from './pages/Resources';
import Inventory from './pages/Inventory';
import Loot from './pages/Loot';
import Progression from './pages/Progression';
import Crafting from './pages/Crafting';
import Combat from './pages/Combat';
import Shop from './pages/Shop';
import Quests from './pages/Quests';
import Achievements from './pages/Achievements';
import Social from './pages/Social';
import Trading from './pages/Trading';
import World from './pages/World';
import LiveOps from './pages/LiveOps';
import Matchmaking from './pages/Matchmaking';
import Leaderboards from './pages/Leaderboards';
import Events from './pages/Events';
import Analytics from './pages/Analytics';
import Simulator from './pages/Simulator';
import Advisor from './pages/Advisor';
import Realms from './pages/Realms';
import GameSettings from './pages/GameSettings';
import UserSettings from './pages/UserSettings';
import Players from './pages/Players';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Onboarding from './pages/Onboarding';
import { api, Module, RealmDefinition, GameInfoAuth, type GameCreated, type PlayerProfile } from './api';
import { BrandMark, ThemeToggle, type ThemeMode } from './components/Theme';

export interface Session {
  gameId: string;
  userId: string;
  userEmail: string;
  playerId: string;
}

interface AuthState {
  token: string | null;
  user: { id: string; email: string } | null;
  games: GameInfoAuth[];
  activeGameId: string | null;
  loading: boolean;
}

function ModuleRoute({
  modules,
  modulesLoaded,
  session,
}: {
  modules: Module[];
  modulesLoaded: boolean;
  session: Session;
}) {
  const { moduleId = '' } = useParams<{ moduleId: string }>();
  if (!modulesLoaded) {
    return (
      <div className="page-container">
        <div className="empty">Loading module...</div>
      </div>
    );
  }
  const mod = modules.find(m => m.id === moduleId);
  if (!mod) {
    return (
      <div className="page-container">
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">Unknown module: {moduleId}</div>
          <div className="text-xs text-fg-muted">Not registered on the backend.</div>
        </div>
      </div>
    );
  }
  if (mod.id === 'resource') return <Resources session={session} />;
  if (mod.id === 'inventory') return <Inventory session={session} />;
  if (mod.id === 'loot') return <Loot session={session} />;
  if (mod.id === 'progression') return <Progression session={session} />;
  if (mod.id === 'crafting') return <Crafting session={session} />;
  if (mod.id === 'combat') return <Combat session={session} />;
  if (mod.id === 'shop') return <Shop session={session} />;
  if (mod.id === 'quest') return <Quests session={session} />;
  if (mod.id === 'achievement') return <Achievements session={session} />;
  if (mod.id === 'social') return <Social session={session} />;
  if (mod.id === 'trading') return <Trading session={session} />;
  if (mod.id === 'world') return <World session={session} />;
  if (mod.id === 'liveops') return <LiveOps session={session} />;
  if (mod.id === 'matchmaking') return <Matchmaking session={session} />;
  if (mod.id === 'leaderboard') return <Leaderboards session={session} />;
  if (mod.id === 'event') return <Events session={session} />;
  if (mod.id === 'analytics') return <Analytics session={session} />;
  if (mod.id === 'simulator') return <Simulator session={session} />;
  if (mod.id === 'advisor') return <Advisor session={session} />;
  return (
    <div className="page-container">
      <PageHeader title={mod.display_name} subtitle={`No SPA page ships for ${mod.id} yet.`} />
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="flex items-end justify-between gap-4 mb-6 pb-4 border-b border-line">
      <div className="min-w-0">
        <h1 className="text-[22px] font-semibold tracking-tight m-0 text-fg">{title}</h1>
        {subtitle && <p className="text-fg-dim text-sm mt-1 m-0">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </header>
  );
}

export function Container({ children }: { children: React.ReactNode }) {
  return (
    <div className="page-container max-w-[1200px] mx-auto px-6 sm:px-8 lg:px-10 py-7 pb-20">
      {children}
    </div>
  );
}

function MobileTopbar({
  onOpen,
  theme,
  onThemeChange,
  games,
  activeGameId,
  onGameChange,
}: {
  onOpen: () => void;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  games: GameInfoAuth[];
  activeGameId: string | null;
  onGameChange: (gameId: string) => void;
}) {
  return (
    <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-bg-1 border-b border-line">
      <button className="btn-ghost btn" onClick={onOpen} aria-label="Open menu">
        <span className="text-lg leading-none">=</span>
      </button>
      <div className="flex items-center gap-2 min-w-0">
        <BrandMark />
        <div className="font-semibold tracking-tight">GameForge</div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {games.length > 1 && (
          <select
            className="game-switcher-select max-w-[150px]"
            value={activeGameId ?? ''}
            onChange={e => onGameChange(e.target.value)}
            aria-label="Switch game"
          >
            {games.map(g => (
              <option key={g.game_id} value={g.game_id}>
                {g.name || g.game_id}
              </option>
            ))}
          </select>
        )}
        <ThemeToggle value={theme} onChange={onThemeChange} />
      </div>
    </div>
  );
}

function GameSwitcher({
  games,
  activeGameId,
  onGameChange,
  onNewGame,
}: {
  games: GameInfoAuth[];
  activeGameId: string | null;
  onGameChange: (gameId: string) => void;
  onNewGame: () => void;
}) {
  const activeGame = games.find(g => g.game_id === activeGameId);
  return (
    <div className="game-switcher">
      <div className="game-switcher-label">Current game</div>
      {games.length > 1 ? (
        <select
          className="game-switcher-select"
          value={activeGameId ?? ''}
          onChange={e => onGameChange(e.target.value)}
          aria-label="Switch game"
        >
          {games.map(g => (
            <option key={g.game_id} value={g.game_id}>
              {g.name || g.game_id}
            </option>
          ))}
        </select>
      ) : (
        <div className="game-switcher-name" title={activeGame?.name}>
          {activeGame?.name || activeGameId || 'No game'}
        </div>
      )}
      <div className="game-switcher-meta">
        {activeGame ? `${activeGame.enabled_packages.length} packages` : 'No packages'}
      </div>
      <button className="btn btn-ghost w-full justify-center text-xs mt-2" onClick={onNewGame}>
        Create new game
      </button>
    </div>
  );
}

function Sidebar({
  modules,
  modulesLoaded,
  isOpen,
  onClose,
  realms,
  selectedRealm,
  onRealmChange,
  games,
  activeGameId,
  onGameChange,
  onLogout,
  onNewGame,
  players,
  activePlayerId,
  onPlayerChange,
  userEmail,
  theme,
  onThemeChange,
}: {
  modules: Module[];
  modulesLoaded: boolean;
  isOpen: boolean;
  onClose: () => void;
  realms: RealmDefinition[];
  selectedRealm: string;
  onRealmChange: (realm: string) => void;
  games: GameInfoAuth[];
  activeGameId: string | null;
  onGameChange: (gameId: string) => void;
  onLogout: () => void;
  onNewGame: () => void;
  players: PlayerProfile[];
  activePlayerId: string | null;
  onPlayerChange: (playerId: string) => void;
  userEmail: string;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
}) {
  const handleNav = () => onClose();

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
      )}
      <aside
        className={[
          'flex flex-col border-r border-line bg-bg-1',
          'fixed lg:sticky top-0 left-0 z-50 h-screen w-[232px]',
          'transition-transform lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
      >
        {/* Brand -- fixed top */}
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-line shrink-0">
          <BrandMark />
          <div className="font-semibold tracking-tight text-[14.5px]">GameForge</div>
        </div>

        <div className="p-3.5 border-b border-line shrink-0">
          <GameSwitcher
            games={games}
            activeGameId={activeGameId}
            onGameChange={onGameChange}
            onNewGame={onNewGame}
          />
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto p-3.5 flex flex-col gap-0.5">
          <div className="text-[10.5px] uppercase text-fg-muted font-semibold tracking-[0.08em] mb-1 mx-2.5">
            Home
          </div>
          <NavLink to="/" end className="nav-link" onClick={handleNav}>
            Dashboard
          </NavLink>
          <div className="text-[10.5px] uppercase text-fg-muted font-semibold tracking-[0.08em] mt-3.5 mb-1 mx-2.5">
            Build config
          </div>
          <NavLink to="/explorer" className="nav-link" onClick={handleNav}>
            Config Explorer
          </NavLink>
          <NavLink to="/drafts" className="nav-link" onClick={handleNav}>
            Drafts &amp; Publish
          </NavLink>
          <NavLink to="/snapshots" className="nav-link" onClick={handleNav}>
            Snapshots
          </NavLink>
          <div className="text-[10.5px] uppercase text-fg-muted font-semibold tracking-[0.08em] mt-3.5 mb-1 mx-2.5">
            Operate
          </div>
          <NavLink to="/audit" className="nav-link" onClick={handleNav}>
            Audit Log
          </NavLink>
          <NavLink to="/realms" className="nav-link" onClick={handleNav}>
            Realms
          </NavLink>
          <NavLink to="/players" className="nav-link" onClick={handleNav}>
            Players
          </NavLink>
          <div className="text-[10.5px] uppercase text-fg-muted font-semibold tracking-[0.08em] mt-3.5 mb-1 mx-2.5">
            Settings
          </div>
          <NavLink to="/settings/game" className="nav-link" onClick={handleNav}>
            Game Settings
          </NavLink>
          <NavLink to="/settings/user" className="nav-link" onClick={handleNav}>
            User Settings
          </NavLink>

          <div className="text-[10.5px] uppercase text-fg-muted font-semibold tracking-[0.08em] mt-3.5 mb-1 mx-2.5">
            Packages
          </div>
          {!modulesLoaded && (
            <div className="nav-link text-fg-muted">Loading...</div>
          )}
          {modulesLoaded && modules.length === 0 && (
            <div className="nav-link text-fg-muted">None registered</div>
          )}
          {modules.map(m => (
            <NavLink
              key={m.id}
              to={`/m/${m.id}`}
              className="nav-link"
              onClick={handleNav}
            >
              {m.display_name}
            </NavLink>
          ))}
        </nav>

        {/* Realm + player context (fixed bottom, above avatar) */}
        <div className="shrink-0 px-3.5 py-3 border-t border-line space-y-2 text-[11.5px]">
          {realms.length > 0 && (
            <div>
              <div className="text-fg-muted text-[10.5px] uppercase tracking-[0.08em] font-semibold mb-0.5">
                Realm
              </div>
              <select
                className="input w-full text-xs"
                value={selectedRealm}
                onChange={e => onRealmChange(e.target.value)}
              >
                <option value="all">All Realms</option>
                {realms.map(r => (
                  <option key={r.realm_id} value={r.realm_id}>
                    {r.display_name} ({r.realm_id})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <div className="text-fg-muted text-[10.5px] uppercase tracking-[0.08em] font-semibold mb-0.5">
              Player
            </div>
            <select
              className="input w-full text-xs min-w-0"
              value={activePlayerId ?? ''}
              onChange={e => onPlayerChange(e.target.value)}
              disabled={players.length === 0}
            >
              {players.length === 0 && <option value="">No players</option>}
              {players.map(player => (
                <option key={player.player_id} value={player.player_id}>
                  {player.display_name}
                </option>
              ))}
            </select>
            {players.length === 0 && (
              <div className="text-fg-muted text-[10px] mt-1">
                Created by your game server.
              </div>
            )}
          </div>
        </div>

        {/* User avatar menu */}
        <UserAvatarMenu
          email={userEmail}
          theme={theme}
          onThemeChange={onThemeChange}
          onLogout={onLogout}
          onClose={onClose}
        />
      </aside>
    </>
  );
}

function UserAvatarMenu({
  email,
  theme,
  onThemeChange,
  onLogout,
  onClose,
}: {
  email: string;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  onLogout: () => void;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-user-menu]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const initial = (email[0] ?? '?').toUpperCase();

  return (
    <div className="shrink-0 p-3 border-t border-line relative" data-user-menu>
      <button
        type="button"
        className="flex items-center gap-2.5 w-full text-left px-2 py-1.5 rounded-md hover:bg-bg-2 transition-colors"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-fg text-bg text-xs font-semibold shrink-0">
          {initial}
        </span>
        <span className="text-xs text-fg truncate flex-1" title={email}>
          {email}
        </span>
        <span className="text-fg-muted text-[10px]">{open ? '▾' : '▴'}</span>
      </button>

      {open && (
        <div className="absolute bottom-full left-3 right-3 mb-2 z-50 rounded-md border border-line bg-bg-1 shadow-lg p-2 text-[12px]">
          <div className="px-2 py-1.5 border-b border-line mb-1">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">Signed in as</div>
            <div className="text-fg truncate" title={email}>{email}</div>
          </div>
          <NavLink
            to="/settings/user"
            className="block px-2 py-1.5 rounded hover:bg-bg-2 text-fg"
            onClick={() => { setOpen(false); onClose(); }}
          >
            User settings
          </NavLink>
          <NavLink
            to="/settings/game"
            className="block px-2 py-1.5 rounded hover:bg-bg-2 text-fg"
            onClick={() => { setOpen(false); onClose(); }}
          >
            Game settings
          </NavLink>
          <div className="border-t border-line my-1" />
          <div className="px-2 py-1">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Theme</div>
            <ThemeToggle value={theme} onChange={onThemeChange} />
          </div>
          <div className="border-t border-line my-1" />
          <button
            type="button"
            className="w-full text-left px-2 py-1.5 rounded hover:bg-bg-2 text-fg-muted"
            onClick={() => { setOpen(false); onLogout(); }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem('forge.theme');
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  });
  const [auth, setAuth] = useState<AuthState>({
    token: localStorage.getItem('forge.token'),
    user: null,
    games: [],
    activeGameId: localStorage.getItem('forge.activeGameId'),
    loading: true,
  });

  const [modules, setModules] = useState<Module[]>([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [realms, setRealms] = useState<RealmDefinition[]>([]);
  const [selectedRealm, setSelectedRealm] = useState('all');
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [activePlayerId, setActivePlayerId] = useState<string | null>(null);
  const [playersLoading, setPlayersLoading] = useState(false);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('forge.token');
    if (!token) {
      setAuth({ token: null, user: null, games: [], activeGameId: null, loading: false });
      return;
    }
    try {
      const res = await api.me();
      const games = res.user.games;
      const savedGameId = localStorage.getItem('forge.activeGameId');
      const activeGameId = games.find(g => g.game_id === savedGameId)?.game_id
        ?? games[0]?.game_id
        ?? null;
      if (activeGameId) localStorage.setItem('forge.activeGameId', activeGameId);
      setAuth({
        token,
        user: { id: res.user.id, email: res.user.email },
        games,
        activeGameId,
        loading: false,
      });
    } catch {
      // Token invalid — clear it
      localStorage.removeItem('forge.token');
      localStorage.removeItem('forge.activeGameId');
      setAuth({ token: null, user: null, games: [], activeGameId: null, loading: false });
    }
  }, []);

  useEffect(() => {
    document.title = 'GameForge Admin';
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    const applyTheme = () => {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.dataset.theme = theme === 'system'
        ? systemDark ? 'dark' : 'light'
        : theme;
    };

    localStorage.setItem('forge.theme', theme);
    applyTheme();

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', applyTheme);
    return () => media.removeEventListener('change', applyTheme);
  }, [theme]);

  useEffect(() => {
    if (!auth.activeGameId) return;
    api
      .modules()
      .then(setModules)
      .catch(() => setModules([]))
      .finally(() => setModulesLoaded(true));
    api.listRealms(auth.activeGameId).then(setRealms).catch(() => setRealms([]));
  }, [auth.activeGameId]);

  useEffect(() => {
    let cancelled = false;
    const gameId = auth.activeGameId;
    const user = auth.user;
    if (!gameId || !user) {
      setPlayers([]);
      setActivePlayerId(null);
      return;
    }

    void user;
    const loadPlayers = async () => {
      setPlayersLoading(true);
      try {
        const list = (await api.listPlayers(gameId)).players;
        if (cancelled) return;
        const storageKey = `forge.activePlayerId.${gameId}`;
        const saved = localStorage.getItem(storageKey);
        const nextPlayerId = list.find(player => player.player_id === saved)?.player_id
          ?? list[0]?.player_id
          ?? null;
        if (nextPlayerId) localStorage.setItem(storageKey, nextPlayerId);
        setPlayers(list);
        setActivePlayerId(nextPlayerId);
      } catch {
        if (!cancelled) {
          setPlayers([]);
          setActivePlayerId(null);
        }
      } finally {
        if (!cancelled) setPlayersLoading(false);
      }
    };

    void loadPlayers();
    return () => { cancelled = true; };
  }, [auth.activeGameId, auth.user]);

  const handleAuth = useCallback((token: string) => {
    setAuth(prev => ({ ...prev, token, loading: true }));
    // Reload user after a tick so token is stored
    setTimeout(() => loadUser(), 50);
  }, [loadUser]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('forge.token');
    localStorage.removeItem('forge.activeGameId');
    setAuth({ token: null, user: null, games: [], activeGameId: null, loading: false });
  }, []);

  const handleGameChange = useCallback((gameId: string) => {
    localStorage.setItem('forge.activeGameId', gameId);
    setAuth(prev => ({ ...prev, activeGameId: gameId }));
    setPlayers([]);
    setActivePlayerId(null);
  }, []);

  const handlePlayerChange = useCallback((playerId: string) => {
    if (!auth.activeGameId) return;
    localStorage.setItem(`forge.activePlayerId.${auth.activeGameId}`, playerId);
    setActivePlayerId(playerId);
  }, [auth.activeGameId]);


  const handleGameUpdated = useCallback(
    (game: { game_id: string; name: string; enabled_packages: string[] }) => {
      setAuth(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.game_id === game.game_id
            ? { ...g, name: game.name, enabled_packages: game.enabled_packages }
            : g,
        ),
      }));
    },
    [],
  );

  const handleGameCreated = useCallback((game: GameCreated) => {
    const created: GameInfoAuth = {
      game_id: game.game_id,
      name: game.name,
      enabled_packages: game.enabled_packages,
    };
    localStorage.setItem('forge.activeGameId', game.game_id);
    setAuth(prev => ({
      ...prev,
      games: [created, ...prev.games.filter(existing => existing.game_id !== game.game_id)],
      activeGameId: game.game_id,
      loading: false,
    }));
    void loadUser();
  }, [loadUser]);

  // Loading state
  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg">
        <div className="text-fg-dim">Loading...</div>
      </div>
    );
  }

  // Not authenticated — show public routes
  if (!auth.token || !auth.user) {
    return (
      <Routes>
        <Route
          path="/login"
          element={<Login onAuth={handleAuth} theme={theme} onThemeChange={setTheme} />}
        />
        <Route
          path="/signup"
          element={<Signup onAuth={handleAuth} theme={theme} onThemeChange={setTheme} />}
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated but no games — send to onboarding
  if (auth.games.length === 0) {
    return (
      <Routes>
        <Route
          path="/onboarding"
          element={
            <Onboarding
              onGameCreated={handleGameCreated}
              theme={theme}
              onThemeChange={setTheme}
            />
          }
        />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes>
    );
  }

  if (playersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg">
        <div className="text-fg-dim">Loading player...</div>
      </div>
    );
  }

  const session: Session = {
    gameId: auth.activeGameId ?? auth.games[0].game_id,
    userId: auth.user.id,
    userEmail: auth.user.email,
    playerId: activePlayerId ?? '',
  };

  return (
    <div className="lg:grid lg:grid-cols-[232px_1fr] min-h-screen bg-bg text-fg">
      <Sidebar
        modules={modules}
        modulesLoaded={modulesLoaded}
        isOpen={navOpen}
        onClose={() => setNavOpen(false)}
        realms={realms}
        selectedRealm={selectedRealm}
        onRealmChange={setSelectedRealm}
        games={auth.games}
        activeGameId={auth.activeGameId}
        onGameChange={handleGameChange}
        onLogout={handleLogout}
        onNewGame={() => window.location.assign('/onboarding')}
        players={players}
        activePlayerId={activePlayerId}
        onPlayerChange={handlePlayerChange}
        userEmail={auth.user.email}
        theme={theme}
        onThemeChange={setTheme}
      />
      <main className="overflow-y-auto min-h-screen">
        <MobileTopbar
          onOpen={() => setNavOpen(true)}
          theme={theme}
          onThemeChange={setTheme}
          games={auth.games}
          activeGameId={auth.activeGameId}
          onGameChange={handleGameChange}
        />
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                session={session}
                games={auth.games}
                activeGameId={auth.activeGameId}
                onGameChange={handleGameChange}
              />
            }
          />
          <Route path="/explorer" element={<Explorer session={session} />} />
          <Route path="/entities/:id" element={<Editor session={session} />} />
          <Route path="/drafts" element={<Drafts session={session} />} />
          <Route path="/snapshots" element={<Snapshots session={session} />} />
          <Route path="/audit" element={<Audit session={session} />} />
          <Route path="/realms" element={<Realms session={session} />} />
          <Route path="/players" element={<Players session={session} />} />
          <Route
            path="/settings/game"
            element={
              <GameSettings
                session={session}
                games={auth.games}
                onGameUpdated={handleGameUpdated}
              />
            }
          />
          <Route
            path="/settings/user"
            element={<UserSettings user={auth.user!} games={auth.games} />}
          />
          <Route
            path="/m/:moduleId"
            element={
              <ModuleRoute
                modules={modules}
                modulesLoaded={modulesLoaded}
                session={session}
              />
            }
          />
          <Route
            path="/onboarding"
            element={
              <Onboarding
                onGameCreated={handleGameCreated}
                theme={theme}
                onThemeChange={setTheme}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
